import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "@storytell/markdown";
import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createEffect, createMemo, Show } from "solid-js";
import { defaultMarkdownOptions } from "../../block/markdownOptions";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { type DriveFilePayload, parseDriveFilePayload } from "./driveFileSchema";
import { DriveIcon } from "./driveIcon";

interface DriveFileRendererProps {
  block: StreamingBlock;
}

const safeDriveUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

// Only Google Docs export as markdown; Sheets come back as CSV and Slides as
// plain text, which the markdown renderer would mangle.
const docMimeType = "application/vnd.google-apps.document";

// The block is already complete when this card renders, so the streamer takes
// the whole document in one append.
const DriveFileMarkdown = (props: { markdown: string }) => {
  const streamer = createSolidStreamingMarkdown();
  let appended = false;
  createEffect(() => {
    if (props.markdown && !appended) {
      streamer.append(props.markdown);
      appended = true;
    }
  });
  return <SolidStreamingMarkdown doc={streamer.doc} options={defaultMarkdownOptions} />;
};

export const DriveFileRenderer = (props: DriveFileRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<DriveFilePayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseDriveFilePayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("drive_file_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
              <DriveIcon class="size-4 shrink-0" />
              <Show
                when={safeDriveUrl(data().webViewLink)}
                fallback={
                  <span class="truncate text-sm font-medium text-primary-text">
                    {data().name || rt.t("drive_file_card.title")}
                  </span>
                }
              >
                {(href) => (
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex min-w-0 items-center gap-1 text-sm font-medium text-brand hover:underline"
                  >
                    <span class="truncate">{data().name || rt.t("drive_file_card.title")}</span>
                    <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                  </a>
                )}
              </Show>
            </div>

            <Show
              when={data().content.trim()}
              fallback={<div class="p-3 text-sm text-muted-text">{rt.t("drive_file_card.empty")}</div>}
            >
              <div class="px-3 py-2">
                <ScrollableFadeContainer maxHeight="200px">
                  <Show
                    when={data().mimeType === docMimeType}
                    fallback={
                      <pre class="font-mono text-xs wrap-break-word whitespace-pre-wrap text-primary-text">
                        {data().content}
                      </pre>
                    }
                  >
                    <DriveFileMarkdown markdown={data().content} />
                  </Show>
                </ScrollableFadeContainer>
              </div>
            </Show>

            <Show when={data().truncated}>
              <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                {rt.t("drive_file_card.truncated")}
              </div>
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
